/**
 * The three steps of the Aged Care Funding Analyser, as a progress strip.
 *
 * WHY THREE STEPS (owner's review with an actuary, September 2026: "this could be daunting to a
 * consumer — advisers would probably be ok"). The analyser used to arrive all at once: the inputs,
 * the walk-in story, the mode chooser and a fourteen-figure plan summary, before the client had done
 * anything. Nothing was wrong with any of it; it was the order. A client has three questions, in
 * order — what does care cost, what does that do to us, and what can we do about it — and the strip
 * puts the screen in that order. Every step is reachable at any time; the strip is a map, not a gate.
 *
 * The strip itself is the shared AnalyserSteps (24 Sep 2026), so every module's three steps look and
 * behave the same; this file only supplies Aged Care's words.
 */
import React from 'react';
import AnalyserSteps, { type AnalyserStep } from '../../components/strategy/AnalyserSteps.tsx';

export type { AnalyserStep };

interface AgedCareStepsProps {
    step: AnalyserStep;
    onStep: (step: AnalyserStep) => void;
    /** The person entering care, for "What it means for Joe". */
    clientName: string;
    /** A change of home: the first step is the new home and the move date. */
    moving?: boolean;
}

const AgedCareSteps = ({ step, onStep, clientName, moving = false }: AgedCareStepsProps) => (
    <AnalyserSteps
        step={step}
        onStep={onStep}
        steps={[
            { title: moving ? 'The new home' : 'What care costs', sub: moving ? 'Its room price and the move date' : 'Room price and start date' },
            { title: `What it means for ${clientName}`, sub: 'Four numbers, and the story behind them' },
            { title: 'Ways to close the gap', sub: 'Recommended plans, or build your own' },
        ]}
    />
);

export default AgedCareSteps;
